import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  ModalHeader,
  ModalFooter,
  HStack,
  Button,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import {
  createDialogContext,
  DialogProvider,
  useDialogWithOptions,
} from "src/Common/Dialog";
import type { KYDAudienceData } from "@common/interfaces/KYDMarketing";
import CreateCustomAudienceFormBody from "../CreateCustomAudienceDialog/CreateCustomAudienceFormBody";
import AudienceSourceStep from "./AudienceSourceStep";
import type { AudienceSource } from "./types";

export const CreateAudienceWizardDialogContext = createDialogContext();

type WizardPhase = "source" | "kyd";

export type CreateAudienceWizardDialogProps = {
  isOpen: boolean;
  close: () => void;
  onCreate: (audience: KYDAudienceData) => void;
  onOpenCsv: () => void;
  onOpenFbIg: () => void;
  onOpenMetaInterests: () => void;
};

const CreateAudienceWizardDialog = ({
  isOpen,
  close,
  onCreate,
  onOpenCsv,
  onOpenFbIg,
  onOpenMetaInterests,
}: CreateAudienceWizardDialogProps) => {
  const [phase, setPhase] = useState<WizardPhase>("source");
  const [selectedSource, setSelectedSource] = useState<AudienceSource | null>(
    null
  );

  const resetWizard = useCallback(() => {
    setPhase("source");
    setSelectedSource(null);
  }, []);

  const handleClose = useCallback(() => {
    resetWizard();
    close();
  }, [close, resetWizard]);

  useEffect(() => {
    if (!isOpen) {
      resetWizard();
    }
  }, [isOpen, resetWizard]);

  const handleSourceNext = () => {
    if (!selectedSource) return;

    switch (selectedSource) {
      case "kyd":
        setPhase("kyd");
        break;
      case "csv":
        handleClose();
        onOpenCsv();
        break;
      case "fb_ig":
        handleClose();
        onOpenFbIg();
        break;
      case "meta_interests":
        // Open interests dialog before closing wizard so Chakra does not dismiss the
        // second modal during the first modal's teardown.
        onOpenMetaInterests();
        handleClose();
        break;
      default:
        break;
    }
  };

  const handleKydCreate = (audience: KYDAudienceData) => {
    onCreate(audience);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent
        bg="white"
        borderRadius="lg"
        p={0}
        minH="40vh"
        data-testid="create-audience-wizard"
      >
        {phase === "source" ? (
          <Box px={6}>
            <ModalHeader px={0}>
              <HStack justify="space-between">
                <Box>
                  <Text fontSize="lg" fontWeight="semibold">
                    Create audience
                  </Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    Select the source for your new audience
                  </Text>
                </Box>
                <IconButton
                  aria-label="Close"
                  icon={<CloseIcon color="gray.500" fontSize={12} />}
                  onClick={handleClose}
                  variant="ghost"
                  color="gray.400"
                  size="xs"
                />
              </HStack>
            </ModalHeader>

            <AudienceSourceStep
              selectedSource={selectedSource}
              onSelect={setSelectedSource}
            />

            <HStack justify="space-between" spacing={3} mt={4} pb={6}>
              <Button
                onClick={handleClose}
                variant="ghost"
                rounded="lg"
                fontSize="16px"
                fontWeight="semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSourceNext}
                bgColor="gray.900"
                color="white"
                _hover={{ bgColor: "black" }}
                isDisabled={!selectedSource}
                rounded="lg"
                fontSize="16px"
                fontWeight="semibold"
              >
                Next
              </Button>
            </HStack>
          </Box>
        ) : (
          <CreateCustomAudienceFormBody
            onCreate={handleKydCreate}
            onClose={handleClose}
            onBackToSource={() => setPhase("source")}
          />
        )}
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

export const CreateAudienceWizardDialogWrapper = ({
  onCreate,
  onOpenCsv,
  onOpenFbIg,
  onOpenMetaInterests,
}: {
  onCreate: (audience: KYDAudienceData) => void;
  onOpenCsv: () => void;
  onOpenFbIg: () => void;
  onOpenMetaInterests: () => void;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    CreateAudienceWizardDialogContext
  );

  return (
    <CreateAudienceWizardDialog
      isOpen={isOpen}
      close={close}
      onCreate={onCreate}
      onOpenCsv={onOpenCsv}
      onOpenFbIg={onOpenFbIg}
      onOpenMetaInterests={onOpenMetaInterests}
    />
  );
};

export const withCreateAudienceWizardDialog =
  <P extends object>(Component: React.ComponentType<P>) =>
  (
    props: P & {
      onCreate?: (audience: KYDAudienceData) => void;
      onOpenCsv?: () => void;
      onOpenFbIg?: () => void;
      onOpenMetaInterests?: () => void;
    }
  ) => {
    return (
      <DialogProvider context={CreateAudienceWizardDialogContext}>
        <Component {...props} />
        <CreateAudienceWizardDialogWrapper
          onCreate={props.onCreate || (() => {})}
          onOpenCsv={props.onOpenCsv || (() => {})}
          onOpenFbIg={props.onOpenFbIg || (() => {})}
          onOpenMetaInterests={props.onOpenMetaInterests || (() => {})}
        />
      </DialogProvider>
    );
  };

export default CreateAudienceWizardDialog;
